import React from "react";
import "./PrivacyPolicy.css";

/* ─── Gap Analysis Data ─────────────────────────────────────────── */


/* ─── Data Use Table ─────────────────────────────────────────────── */
const dataUse = [
  { purpose: "Event registration", data: "Name, email, phone, ticket type", basis: [{ label: "Contract", variant: "blue" }], retention: "3 years after event" },
  { purpose: "Payment processing", data: "M-Pesa/card reference, transaction amount", basis: [{ label: "Contract", variant: "blue" }], retention: "7 years (tax)" },
  { purpose: "Event updates (opted in)", data: "Name, email, phone", basis: [{ label: "Consent", variant: "orange" }], retention: "Until you unsubscribe" },
  { purpose: "Safety & security / CCTV", data: "CCTV footage, incident records", basis: [{ label: "Legitimate interests", variant: "green" }], retention: "30 days CCTV; 3 yrs incidents" },
  { purpose: "Photography / video at events", data: "Images, video footage", basis: [{ label: "Consent", variant: "orange" }, { label: "Legitimate interests", variant: "green" }], retention: "3 years or until you object" },
  { purpose: "Website analytics", data: "IP address, browser type, page views", basis: [{ label: "Legitimate interests", variant: "green" }], retention: "12 months" },
  { purpose: "Marketing (opted in)", data: "Name, email, phone, preferences", basis: [{ label: "Consent", variant: "orange" }], retention: "Until consent withdrawn" },
  { purpose: "Tax & legal compliance", data: "Financial and identity records", basis: [{ label: "Legal obligation", variant: "blue" }], retention: "7 years minimum" },
  { purpose: "Children's data", data: "Name, age range, guardian contact", basis: [{ label: "Parental consent", variant: "orange" }], retention: "Event duration + 1 year" },
];

/* ─── Rights Data ────────────────────────────────────────────────── */
const rights = [
  { title: "Access your data", desc: "Request a copy of all personal data we hold about you.", time: "Within 30 days" },
  { title: "Correct your data", desc: "Ask us to fix inaccurate or outdated information.", time: "Within 14 days" },
  { title: "Delete your data", desc: "Ask us to erase data where there is no longer a lawful basis.", time: "Within 14 days" },
  { title: "Object to processing", desc: "Object to marketing or processing based on legitimate interests.", time: "Immediately" },
  { title: "Withdraw consent", desc: "Withdraw marketing or other consent at any time.", time: "Immediately" },
  { title: "Data portability", desc: "Request your data in a structured, machine-readable format.", time: "Within 30 days" },
  { title: "Lodge a complaint", desc: "File a complaint with the ODPC if we've mishandled your data.", time: "odpc.go.ke", isLink: true },
];

/* ─── Sub-components ─────────────────────────────────────────────── */
const RiskBadge = ({ level }) => (
  <span className={`pp-risk ${level.toLowerCase()}`}>{level}</span>
);

const LegalBadge = ({ label, variant }) => (
  <span className={`pp-badge ${variant === "green" ? "green" : variant === "blue" ? "blue" : ""}`}>
    {label}
  </span>
);

const Section = ({ num, title, children, noBorder = false }) => (
  <div className={`pp-section${noBorder ? " no-border" : ""}`}>
    <p className="pp-section-num">{num}</p>
    <h2>{title}</h2>
    {children}
  </div>
);

/* ─── Main Component ─────────────────────────────────────────────── */
const PrivacyPolicy = () => {
  return (
    <div className="pp-wrap">
      <div className="pp-inner">

        {/* ── Header ── */}
        <header className="pp-header">
          <span className="pp-eyebrow">Legal</span>
          <h1 className="pp-title">Privacy Policy</h1>
          <p className="pp-subtitle">Biz Baz Events Limited &nbsp;·&nbsp; Effective 13 January 2026</p>
        </header>

        {/* ── Quick Summary ── */}
        <div className="pp-summary">
          <div className="pp-summary-icon">
            <svg viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
              <circle cx="10" cy="10" r="9" stroke="white" strokeWidth="1.5" />
              <path d="M10 9v5M10 6.5v.5" stroke="white" strokeWidth="1.8" strokeLinecap="round" />
            </svg>
          </div>
          <div>
            <p className="pp-summary-label">Quick summary</p>
            <p className="pp-summary-text">
              We collect your data to run our events and, if you choose, keep you updated.{" "}
              <strong>We do not sell your data.</strong> You can access, update, or delete
              your data at any time by emailing{" "}
              <strong>dpo@bizbazevents.com</strong>. This policy is governed by Kenya's
              Data Protection Act, 2019.
            </p>
          </div>
        </div>

        {/* ════════════════════════════════════════
            PART 1 — GAP ANALYSIS
        ════════════════════════════════════════ */}
        
        {/* ════════════════════════════════════════
            PART 2 — REWRITTEN POLICY
        ════════════════════════════════════════ */}

        {/* 1 — Who We Are */}
        <Section num="01" title="Who we are">
          <p>
            Biz Baz Events Limited is a Kenyan company that curates and produces festivals,
            markets, pop-ups, and live experiences — including the Bizarre Bazaar Festival.
          </p>
          <p>
            For purposes of the Data Protection Act, 2019, we are the <strong>data controller</strong>{" "}
            for personal data collected through our website (
            <a href="https://bizbazevents.com" target="_blank" rel="noopener noreferrer">
              bizbazevents.com
            </a>
            ), event registrations, ticketing platforms, social media, and at our events.
          </p>
        </Section>

        {/* 2 — What Data We Collect */}
        <Section num="02" title="What data we collect">
          <h3>Data you give us directly</h3>
          <ul>
            <li>Full name, email address, phone number</li>
            <li>Date of birth or age range (age-restricted events)</li>
            <li>
              Payment reference details (M-Pesa confirmation, card last four digits — we never
              store full card numbers)
            </li>
            <li>Dietary requirements, accessibility needs, or disability information if volunteered</li>
            <li>Communications, complaints, and feedback you send us</li>
          </ul>
          <h3>Data collected at our events</h3>
          <ul>
            <li>CCTV footage at venues (security purposes)</li>
            <li>Photographs and video by official photographers and videographers — see Section 6</li>
            <li>Incident or medical assistance records where required</li>
          </ul>
          <h3>Data from our website</h3>
          <ul>
            <li>IP address, browser and device information</li>
            <li>Pages visited, time spent, links clicked (via cookies — see Section 4)</li>
            <li>Registration and login data for event platforms</li>
          </ul>
          <h3>Children's data</h3>
          <p>
            We process data for children under 18 only where a parent or guardian has given
            explicit consent. We collect only the minimum data necessary — typically the child's
            name, age range, and the guardian's contact details.
          </p>
        </Section>

        {/* 3 — Why We Use Your Data */}
        <Section num="03" title="Why we use your data — and our legal basis">
          <p>
            The Kenya Data Protection Act, 2019 requires us to have a valid legal basis for
            each processing activity. The table below maps each purpose to its legal basis and
            retention period.
          </p>
          <div className="pp-table-wrap">
            <table className="pp-table">
              <thead>
                <tr>
                  <th>Purpose</th>
                  <th>Data used</th>
                  <th>Legal basis</th>
                  <th>Retention</th>
                </tr>
              </thead>
              <tbody>
                {dataUse.map((row, i) => (
                  <tr key={i}>
                    <td>{row.purpose}</td>
                    <td>{row.data}</td>
                    <td>
                      {row.basis.map((b, j) => (
                        <LegalBadge key={j} label={b.label} variant={b.variant} />
                      ))}
                    </td>
                    <td>{row.retention}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Section>

        {/* 4 — Cookies */}
        <Section num="04" title="Cookies">
          <p>
            Our website uses four categories of cookies. You will be asked for consent on your
            first visit and can update preferences at any time via the cookie settings link in
            our footer.
          </p>
          <ul>
            <li><strong>Essential</strong> — required for the site to work (login sessions, ticket cart). Cannot be turned off.</li>
            <li><strong>Functional</strong> — remember your preferences (language, location). You can disable these.</li>
            <li><strong>Analytics</strong> — help us understand site usage via aggregated, anonymised data (e.g. Google Analytics).</li>
            <li><strong>Marketing</strong> — used by advertising partners to show relevant content. Only active with your explicit consent.</li>
          </ul>
        </Section>

        {/* 5 — Who We Share Data With */}
        <Section num="05" title="Who we share your data with">
          <p>We <strong>do not sell your data</strong>. We share it only with:</p>
          <ul>
            <li><strong>Ticketing platforms</strong>  — to manage registration and tickets</li>
            <li><strong>Payment processors</strong>  — to complete transactions securely</li>
            <li><strong>Email platforms</strong> — to send updates you have consented to</li>
            <li><strong>Media partners</strong> — under agreements restricting use to Biz Baz Events purposes only</li>
            <li><strong>Security &amp; venue partners</strong> — minimum data required for event safety</li>
            <li><strong>Legal or regulatory bodies</strong> — when required by Kenyan law or court order</li>
            <li><strong>A new owner</strong> — only if Biz Baz Events is sold or transferred; you will be notified</li>
          </ul>
        </Section>

        {/* 6 — Photography */}
        <Section num="06" title="Photography, video and CCTV at events">
          <ul>
            <li>
              <strong>Editorial use</strong> — we may use event images on our website, social
              media, and marketing based on legitimate interests (standard practice at public
              cultural events).
            </li>
            <li>
              <strong>Your options</strong> — if you do not wish to appear in our materials,
              inform our team at the event or email{" "}
              <a href="mailto:dpo@bizbazevents.com">dpo@bizbazevents.com</a>.
            </li>
            <li>
              <strong>Commercial use</strong> — if we wish to use your image in paid
              advertising, we will seek your explicit written consent separately.
            </li>
            <li>
              <strong>CCTV</strong> — operated for security; footage retained for 30 days
              unless required for an incident investigation.
            </li>
            <li>
              <strong>Children</strong> — we never use identifiable photographs of children in
              marketing without explicit written parental consent.
            </li>
          </ul>
        </Section>

        {/* 7 — International Transfers */}
        <Section num="07" title="International data transfers">
          <p>
            Some service providers are based outside Kenya. We ensure your data is protected
            through Standard Contractual Clauses (SCCs) or by using providers in countries with
            equivalent data protection laws.
          </p>
          <ul>
            <li><strong>Mailchimp</strong> (Intuit Inc., USA) — SCCs in place</li>
            <li><strong>Stripe</strong> (USA/EU) — SCCs in place</li>
            <li><strong>Google Analytics</strong> (USA) — anonymised data, SCCs in place</li>
          </ul>
          <p>This list will be updated as our service providers change.</p>
        </Section>

        {/* 8 — Your Rights */}
        <Section num="08" title="Your rights under Kenyan law">
          <p>Under the Kenya Data Protection Act, 2019, you have the following rights:</p>
          <div className="pp-rights">
            {rights.map((r, i) => (
              <div className="pp-right-card" key={i}>
                <p className="pp-right-title">{r.title}</p>
                <p className="pp-right-desc">{r.desc}</p>
                {r.isLink ? (
                  <a
                    href="https://www.odpc.go.ke"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="pp-right-time"
                    style={{ textDecoration: "none" }}
                  >
                    {r.time}
                  </a>
                ) : (
                  <span className="pp-right-time">{r.time}</span>
                )}
              </div>
            ))}
          </div>
          <p>
            To exercise any right, email{" "}
            <a href="mailto:dpo@bizbazevents.com">dpo@bizbazevents.com</a>. We will
            not charge you and may need to verify your identity before processing your request.
          </p>
        </Section>

        {/* 9 — Data Breaches */}
        <Section num="09" title="Data breaches">
          <p>
            If a data breach occurs that is likely to affect your rights or freedoms, we will
            notify the Office of the Data Protection Commissioner (ODPC) as soon as reasonably
            practicable and, where required, notify you directly. We maintain an internal breach
            register and conduct post-breach reviews.
          </p>
        </Section>

        {/* 10 — Security */}
        <Section num="10" title="How we protect your data">
          <ul>
            <li>Encrypted data storage and secure transmission (HTTPS)</li>
            <li>Access controls — data accessible only to staff who need it</li>
            <li>Third-party processor agreements with data security obligations</li>
            <li>Regular reviews of our data processing activities</li>
          </ul>
          <p>
            No digital system is 100% secure. If you have concerns, contact our DPO immediately.
          </p>
        </Section>

        {/* 11 — Updates */}
        <Section num="11" title="Updates to this policy">
          <p>
            We may update this policy as our services change or legal requirements evolve.
            Material changes will be communicated by email or a notice on our website. The
            effective date at the top of this document always reflects the most recent version.
          </p>
        </Section>

        <div className="pp-divider" />

        {/* 12 — Contact */}
        <Section num="12" title="Contact and complaints" noBorder>
          <div className="pp-contact">
            <div className="pp-contact-col">
              <p className="pp-contact-label">Data Protection Officer</p>
              <p className="pp-contact-detail">Biz Baz Events Limited</p>
            </div>
            <div className="pp-contact-col">
              <p className="pp-contact-label">Email</p>
              <p className="pp-contact-detail">
                <a href="mailto:dpo@bizbazevents.com">dpo@bizbazevents.com</a>
              </p>
            </div>
            <div className="pp-contact-col">
              <p className="pp-contact-label">Phone</p>
              <p className="pp-contact-detail">0701 395 902</p>
            </div>
            <div className="pp-contact-col">
              <p className="pp-contact-label">Post</p>
              <p className="pp-contact-detail">
                P.O. Box 57250-00200<br />Nairobi, Kenya
              </p>
            </div>
          </div>
          <p style={{ marginTop: "1rem", fontSize: "0.83rem", color: "#7a6a55" }}>
            If you are not satisfied with our response, you may lodge a complaint with the{" "}
            <strong>Office of the Data Protection Commissioner (ODPC)</strong> at{" "}
            <a
              href="https://www.odpc.go.ke"
              target="_blank"
              rel="noopener noreferrer"
            >
              www.odpc.go.ke
            </a>{" "}
            — Britam Towers, 12th Floor, Hospital Road, Upperhill, Nairobi.
          </p>
        </Section>

      </div>
    </div>
  );
};

export default PrivacyPolicy;